<template>
    <div
        ref="layout"
        class="widget-card-layout"
        :class="{
            'widget-card-layout--squashed': squashed,
            'widget-card-layout--inline': options.inline,
            'widget-card-layout--relative': relative
        }"
    >
        <slot name="hoisted" />

        <Loading v-if="loading" />
        <div
            v-else
            ref="content"
            class="widget-card-content"
            data-testid="widget-content"
        >
            <div class="content-top">
                <IconSvg
                    :icon="icon"
                    circle
                />
                <slot />
            </div>

            <div
                v-if="slots.bottom"
                class="page-bottom"
            >
                <slot name="bottom" />
            </div>
        </div>

        <div
            v-if="slots.actions"
            class="content-actions"
            data-testid="widget-actions"
        >
            <slot name="actions" />
        </div>
    </div>
</template>

<script setup lang="ts">
import { onMounted, ref, useSlots } from 'vue'
import IconSvg from './IconSvg.vue'
import useScrollShadow from '@/composables/scroll-shadow'
import useWidgetOptions from '@/composables/widget-options'
import Loading from '@/views/Loading.vue'

const slots = useSlots()
const { options } = useWidgetOptions()

defineProps<{
    icon?: string | null
    squashed?: boolean
    relative?: boolean
    loading?: boolean
}>()

const content = ref<HTMLElement | null>(null)
const layout = ref<HTMLElement | null>(null)

onMounted(() => {
    if (!options.value.inline) {
        useScrollShadow(content.value as HTMLElement, layout.value as HTMLElement)
    }
})
</script>

<style lang="scss" scoped>
.widget-card-layout {
    flex: 1;
    display: flex;
    flex-direction: column;
    min-height: 0;
    padding: 0;

    &--squashed {
        padding: 0;

        & .widget-card-content {
            padding: 0;
            padding-bottom: 12px;
        }
    }

    &--inline {
        --widget-border-radius: 0;

        .widget-card-content {
            padding-top: 20px;
        }
    }

    &--relative {
        position: relative;
    }

}
.widget-card-content {
    flex: 1;
    display: flex;
    flex-direction: column;
    overflow: auto;
    padding: 20px;
    border-radius: var(--widget-border-radius);
    background-color: var(--tl-widget-background-color);
    background-image: var(--tl-widget-background-url);
    background-size: cover;
}

.content-top {
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-content: center;
}

.content-actions {
    position: sticky;
    padding: 12px;
    padding-top: 0;
    bottom: 0;
    background: var(--tl-widget-background-color, var(--color-white, #fff));
}

</style>
